import type React from 'react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import {
  AppButton,
  Modal,
  SearchableSelect,
  type SearchableSelectOption,
} from '@/components/framework'
import {
  getAssignmentValidationError,
  getAssignmentValidationMessageParams,
} from '@/lib/timeline/assignmentValidation'
import { UNASSIGNED_ROW_ID } from '@/lib/timeline/constants'
import type { CallsignRow, Request } from '@/types/timeline'

interface Props {
  isOpen: boolean
  request: Request | null
  assignedCallsignName?: string
  callsigns: CallsignRow[]
  onClose: () => void
  onAssign: (requestId: string, callsignId: string) => void
  onUnassign: (requestId: string) => void
}

export const RequestDetailModal: React.FC<Props> = ({
  isOpen,
  request,
  assignedCallsignName,
  callsigns,
  onClose,
  onAssign,
  onUnassign,
}) => {
  const { t } = useTranslation()
  const [selectedCallsignId, setSelectedCallsignId] = useState('')
  const wasOpenRef = useRef(false)

  const isAssigned = Boolean(assignedCallsignName)

  const currentCallsignId =
    callsigns.find((callsign) => callsign.requests.some((r) => r.id === request?.id))?.id ?? ''

  const assignableCallsigns = useMemo(
    () => callsigns.filter((callsign) => callsign.id !== UNASSIGNED_ROW_ID),
    [callsigns]
  )

  const assignmentOptions = useMemo<SearchableSelectOption[]>(() => {
    const callsignOptions = assignableCallsigns
      .filter((callsign) => Boolean(callsign.name?.trim()))
      .map((callsign) => ({
        id: `callsign:${callsign.id}`,
        value: callsign.id,
        label: callsign.name,
        leadingLabel: callsign.staffName?.trim() ?? '',
        trailingLabel: callsign.name,
        groupLabel: t('requestAssign.callsignsGroup'),
      }))

    const staffOptions = assignableCallsigns
      .filter((callsign) => {
        const staffName = callsign.staffName?.trim()
        const callsignName = callsign.name?.trim() ?? ''
        return Boolean(staffName) && !callsignName
      })
      .map((callsign) => ({
        id: `staff:${callsign.id}`,
        value: callsign.id,
        label: callsign.staffName?.trim() ?? '',
        groupLabel: t('requestAssign.staffGroup'),
      }))

    return [...callsignOptions, ...staffOptions]
  }, [assignableCallsigns, t])

  useEffect(() => {
    const justOpened = isOpen && !wasOpenRef.current
    wasOpenRef.current = isOpen
    if (justOpened) setSelectedCallsignId(currentCallsignId)
  }, [isOpen, currentCallsignId])

  const handleClose = () => {
    setSelectedCallsignId('')
    onClose()
  }

  const handleAssignConfirm = () => {
    if (!request || !selectedCallsignId) return

    const targetCallsign = callsigns.find((callsign) => callsign.id === selectedCallsignId)
    if (targetCallsign) {
      const validationError = getAssignmentValidationError(request, targetCallsign)
      if (validationError) {
        toast.error(
          t(
            `assignmentValidation.${validationError}`,
            getAssignmentValidationMessageParams(request, targetCallsign)
          )
        )
        return
      }
    }

    onAssign(request.id, selectedCallsignId)
  }

  const handleUnassign = () => {
    if (!request) return
    onUnassign(request.id)
  }

  if (!request) return null

  const routeParts = [request.boardStationCode, request.alightStationCode].filter(Boolean)
  const routeLine = routeParts.join(' → ')

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={t('requestAssign.detailsTitle')} size="md">
      <div className="space-y-4">
        {routeLine && <div className="text-base font-semibold text-gray-900">{routeLine}</div>}

        <div className="flex items-center gap-2">
          <span
            className={`rounded border px-2 py-0.5 text-xs font-bold tracking-wide uppercase ${
              request.type === 'ARR'
                ? 'border-brand bg-arrival-bg text-arrival-text'
                : 'border-amber-600 bg-departure-bg text-departure-text'
            }`}
          >
            {request.type}
          </span>
          <span className="text-sm text-gray-500 capitalize">
            {request.status.toLowerCase().replace('_', ' ')}
          </span>
        </div>

        {request.customerName && (
          <div className="text-sm font-medium text-gray-800">{request.customerName}</div>
        )}

        <div className="space-y-1 text-sm text-gray-700">
          <div>
            {request.startTime} – {request.endTime}
          </div>
          {request.scheduledTime && (
            <div className="text-gray-500">
              {t('timeline.tooltip.scheduled')}: {request.scheduledTime}
            </div>
          )}
        </div>

        <div className="text-sm">
          {isAssigned ? (
            <span className="text-gray-700">
              {t('requestAssign.assignedTo', { name: assignedCallsignName })}
            </span>
          ) : (
            <span className="text-gray-600 italic">{t('requestAssign.unassigned')}</span>
          )}
        </div>

        {request.additionalInfo && (
          <div className="text-sm text-gray-500">{request.additionalInfo}</div>
        )}
        {request.assistanceDetails && (
          <div className="text-sm text-gray-500">{request.assistanceDetails}</div>
        )}

        <div className="pt-2">
          <label
            htmlFor="request-detail-assign-callsign"
            className="mb-1 block text-sm font-medium text-gray-700"
          >
            {t('requestAssign.selectCallsign')}
          </label>
          <SearchableSelect
            id="request-detail-assign-callsign"
            selectedValue={selectedCallsignId}
            options={assignmentOptions}
            onChange={(option) => setSelectedCallsignId(option.value)}
            placeholder={t('requestAssign.selectCallsignPlaceholder')}
            emptyText={t('requestAssign.noOptions')}
            clearButtonAriaLabel={t('requestAssign.clearSelection')}
            onClear={() => setSelectedCallsignId('')}
          />
        </div>

        <div className="mt-4 flex items-center justify-between gap-2 border-t border-gray-200 pt-4">
          <div>
            {isAssigned && (
              <AppButton variant="danger" size="md" onClick={handleUnassign}>
                {t('requestAssign.unassign')}
              </AppButton>
            )}
          </div>
          <div className="flex items-center gap-2">
            <AppButton variant="secondary" size="md" onClick={handleClose}>
              {t('requestAssign.cancel')}
            </AppButton>
            <AppButton
              variant="primary"
              size="md"
              onClick={handleAssignConfirm}
              disabled={!selectedCallsignId || selectedCallsignId === currentCallsignId}
            >
              {isAssigned ? t('requestAssign.reassign') : t('requestAssign.assign')}
            </AppButton>
          </div>
        </div>
      </div>
    </Modal>
  )
}
